/**
 * WHERE THIS JOB IS, AND THE ONE THING TO DO NEXT.
 *
 * Anthony liked two things on the old job card and both are here: the
 * track that runs from the call to the money, so anybody glancing at the
 * job knows where it stands without reading it, and the line that tells
 * them what to do next.
 *
 * The rule for "next" is that there is exactly ONE of it. A card offering
 * six equally-weighted buttons is the button overload problem in
 * miniature: every option present, no path obvious. So this returns a
 * single action and the REASON for it, because a person who understands
 * why does the right thing next time without being told.
 *
 * The order is the shop's order and it is not negotiable by the interface:
 * no work without an authorization, no booking without an approval, no
 * bill without the work being done. Those are the rules the last system
 * let people click past.
 */

import type { Status } from "../data/jobs.ts";
import { words } from "./trade.ts";

/**
 * THE BUTTON THAT DOES THE NEXT THING.
 *
 * Anthony: "on job card, there is no option for the tech to click to alert
 * customer they are en route or to mark they are starting job. these
 * should be clear buttons to move them down the path."
 *
 * He is right, and the reason was a rule taken too far. The card said what
 * to do next and then made you find the noun for it in a row of seven
 * stage chips near the bottom — which is fine for an advisor who knows the
 * vocabulary and useless to a technician standing at a car in the sun.
 * Telling somebody what to do and not giving them the button is worse than
 * either half on its own.
 *
 * ONE PRIMARY MOVE, still. The rule that made the card readable was one
 * next action, and that survives: `move` is the single thing this job
 * wants. `alsoTells` is not a second stage — it is a message to the
 * customer, which changes nothing about the job and can never be the wrong
 * button to press.
 */
export type Move =
  /** Set the status. The usual protocol gate applies on the way. */
  | { kind: "status"; to: Status; label: string }
  /** Raise the bill, then mark it billed. */
  | { kind: "invoice"; label: string }
  /** Put the cursor in the appointment box; there is nothing to set yet. */
  | { kind: "book"; label: string }
  /** Take them to the line editor, which is where a price comes from. */
  | { kind: "price"; label: string }
  /** Open the authorization form, folded shut on a phone. */
  | { kind: "authorize"; label: string }
  /** Take them to the payment box. */
  | { kind: "pay"; label: string }
  /** Take them to the prepayment card: money before the work, job stays open. */
  | { kind: "prepay"; label: string };

export type Step = {
  /** Zero-based position on the track. */
  at: number;
  /** What to do, in the imperative, six words or fewer. */
  action: string;
  /** Why it is the next thing. One sentence. */
  because: string;
  /** True when the job is finished and there is nothing to chase. */
  done: boolean;
  /** True when the job is stopped rather than progressing. */
  halted: boolean;
  /** The button for `action`, when it is one press. */
  move?: Move;
  /** True when "on my way" is a thing worth offering — booked, not started. */
  alsoTells?: boolean;
};

/** The track everybody watches. Eight stops, call to cleared. */
export const TRACK = [
  "Enquiry", "Quoted", "Approved", "Booked", "Working", "Done", "Billed", "Paid",
] as const;

export type Facts = {
  status: Status;
  hasPricedLine: boolean;
  hasAuthorization: boolean;
  scheduledAt: string | null;
  hasInvoice: boolean;
  invoicePaid: boolean;
  /**
   * Enough on the record to say WHICH one was worked on. Closing a job
   * without that is closing it blind. What satisfies it varies by trade --
   * see isIdentified() in domain/trade.ts.
   */
  carIdentified?: boolean;
  /** Decides the words the refusal uses. Omitted reads as 'auto'. */
  trade?: string | null;
  /**
   * THE QUOTE IS A FREE ESTIMATE VISIT, and nothing else is on it.
   *
   * Anthony, 2026-09-29, about Swift: "we just want a quote to be the
   * customer saying yes to a free visit for an estimate ... Next step is
   * schedule still." A visit has no priced line by design, so every rule
   * below that reads "nothing is priced, so there is nothing to agree to"
   * would have held it at step one for ever. For a visit the yes is to the
   * visit, and booking it is the next thing, exactly as after a priced
   * yes. Starting WORK still wants a real price: the technician quotes the
   * door on site and that is a second, priced yes.
   *
   * Set from isVisitOnly() in src/domain/visitquote.ts. Omitted reads as
   * false, which is every job before this existed.
   */
  visitQuote?: boolean;
  /**
   * A PRE-PURCHASE INSPECTION, PAID BEFORE IT IS DONE.
   *
   * Anthony, 2026-10-07. The money on a PPI comes first, mostly by Zelle,
   * and this card said nothing about it until the job was Done -- so
   * staff marked inspections Done before they happened to get a bill.
   * With `ppi` set, the step after the yes is "Take the prepayment" until
   * `prepaid`. Omitted reads as false: every other job is unchanged.
   */
  ppi?: boolean;
  prepaid?: boolean;
};

/**
 * WHY YOU ARE STUCK, BEFORE YOU PRESS ANYTHING.
 *
 * Anthony: "we don't have a what's next option that explains what they
 * need to do if they get stuck mid funnel."
 *
 * Every state has a button now, and pressing it when something is missing
 * gets you the protocol gate — which is correct, and is also the first
 * time anybody finds out. A technician standing at a car should be able to
 * read what is missing without pressing a button to be told no.
 *
 * These are the SAME rules `setStatus` enforces, said in advance rather
 * than after the fact. Each one is a thing to go and do, not a complaint:
 * "no plate and no VIN on the car" is a sentence somebody can act on
 * standing where they are.
 *
 * It is deliberately not a permission check. What blocks the job blocks
 * everybody; who may step past it is a different question and lives in
 * permissions.ts.
 */
export function whatBlocks(f: Facts, to: Status): string[] {
  const stuck: string[] = [];

  if (to === "in_progress" && !f.hasAuthorization) {
    stuck.push("Nobody has said yes yet. Signed or over the phone, it has to exist before the work does.");
  }
  if (to === "scheduled" && !f.scheduledAt) {
    stuck.push("It has no day on it, so it would sit on no board.");
  }
  if (["completed", "invoiced", "paid"].includes(to)) {
    if (!f.hasAuthorization) stuck.push("There is no authorization to close it against.");
    if (!f.carIdentified) stuck.push(words(f.trade).identifyBlocked);
  }
  if (["invoiced", "paid"].includes(to) && !f.hasInvoice) {
    stuck.push("There is no invoice yet, so the customer has no bill to pay and nothing to keep.");
  }
  /* A free visit is booked, not priced; everything past the booking still
     needs the real price the technician writes at the door. */
  if (to !== "estimate" && !f.hasPricedLine && !(f.visitQuote && to === "scheduled")) {
    stuck.push("Nothing on it has a price, so there is no figure to agree to.");
  }
  return stuck;
}

export function nextStep(f: Facts): Step {
  const step = (at: number, action: string, because: string,
                extra: Partial<Step> = {}): Step =>
    ({ at, action, because, done: false, halted: false, ...extra });

  if (f.status === "cancelled") {
    return step(0, "Nothing — this is cancelled", "Reopen it only if the customer came back.",
      { halted: true });
  }

  if (f.status === "estimate" && f.visitQuote && !f.hasPricedLine) {
    if (!f.hasAuthorization) {
      return step(1, "Get their yes to the visit",
        "It is a free estimate visit, so there is no price to agree to. Their yes is to us coming out.",
        { move: { kind: "authorize", label: "Record the yes" } });
    }
    return step(2, "Book the visit",
      "They said yes to the free estimate. It needs a day, or it will sit here looking like a quote.",
      { move: { kind: "book", label: "Give it a day" } });
  }

  if (f.status === "estimate") {
    if (!f.hasPricedLine) {
      return step(0, "Put a price on it",
        "Nobody can approve a figure they have not been shown, so this is the first real step.",
        { move: { kind: "price", label: "Add labor or a part" } });
    }
    if (!f.hasAuthorization) {
      return step(1, "Get the customer's yes",
        "Signed, or over the phone with their name recorded. Both count. Neither is optional before work starts.",
        { move: { kind: "authorize", label: "Record the yes" } });
    }
    if (f.ppi && !f.prepaid) {
      return step(2, "Take the prepayment",
        "A pre-purchase inspection is paid before it is done. It goes on its own invoice, so the job stays open until it is inspected.",
        { move: { kind: "prepay", label: "Take the prepayment" } });
    }
    return step(2, "Book it in",
      "It is approved and priced. It needs a day, or it will sit here looking like a quote.",
      { move: { kind: "book", label: "Give it a day" } });
  }

  if (f.status === "scheduled") {
    if (!f.scheduledAt) {
      return step(3, "Give it a date",
        "Booked with no date is on no board and will chase nobody. This is the state that used to go quiet.",
        { move: { kind: "book", label: "Give it a day" } });
    }
    /* A booked free visit is a technician going to look, not work about to
       start. What happens at the door is a real price and a second yes. */
    if (f.visitQuote && !f.hasPricedLine) {
      return step(3, "Price it at the door",
        "This is a free estimate visit. Write up what it needs, and get their yes to that price before any work.",
        { move: { kind: "price", label: "Add labor or a part" } });
    }
    if (!f.hasAuthorization) {
      return step(1, "Get the customer's yes",
        "This is booked without an authorization on file. The work should not start until there is one.",
        { move: { kind: "authorize", label: "Record the yes" } });
    }
    if (f.ppi && !f.prepaid) {
      return step(3, "Take the prepayment",
        "Booked, and not paid yet. A pre-purchase inspection is paid before the technician goes out.",
        { move: { kind: "prepay", label: "Take the prepayment" } });
    }
    return step(3, "Start it when the day comes",
      "Everything it needs is on it.",
      { move: { kind: "status", to: "in_progress", label: "Start the job" }, alsoTells: true });
  }

  if (f.status === "in_progress") {
    return step(4, "Finish and mark it done",
      "Anything found along the way needs its own authorization before it is worked on.",
      { move: { kind: "status", to: "completed", label: "Mark it done" } });
  }

  if (f.status === "completed") {
    return f.hasInvoice
      ? step(6, "Chase the payment", "The bill is out. Nothing else moves until it is settled.",
          { move: { kind: "pay", label: "Record a payment" } })
      // Cash is an answer, not an exception. The old system had no way to
      // say "they paid at the curb", so hundreds of finished jobs look
      // unbilled when the money is long since in the till. Asking the
      // question both ways is the fix.
      : step(5, "Bill it, or record how it was paid",
          "Finished, with nothing written down about the money. If they paid cash, that is a payment — record it and it closes.",
          { move: { kind: "invoice", label: "Raise the invoice" } });
  }

  if (f.status === "invoiced") {
    return f.invoicePaid
      ? step(7, "Nothing — it is paid", "Closed out.", { done: true })
      : step(6, "Take the payment", "Billed and unpaid. Every day here is a day of the shop's money.",
          { move: { kind: "pay", label: "Record a payment" } });
  }

  if (f.status === "paid") {
    return step(7, "Nothing — it is paid", "Closed out.", { done: true });
  }

  return step(0, "Look at it", "This job is in a state the desk does not recognize.");
}

/**
 * THE FIVE STARS.
 *
 * Anthony asked for five stars filling in from the call to the money, on
 * the job card, "for everyone to watch". Five and not eight, because this
 * is the glanceable version: somebody walking past a screen should read it
 * without stopping, and eight dots is a diagram you have to study.
 *
 * They are milestones, not steps. A star is EARNED and does not un-earn
 * itself — a job that has been authorized stays authorized even after it
 * is finished — which is what makes the row honest at a glance. The eight
 * named stops in TRACK are still the detail underneath; these are the
 * headline.
 *
 * Each one is a fact about the job, not a status value, because status
 * lies: the old system had jobs marked `scheduled` that nothing had ever
 * been agreed on. "Is there an authorization" cannot lie.
 */

export type Milestone = { label: string; earned: boolean; what: string };

export function milestones(f: Facts & { hasInvoice: boolean; invoicePaid: boolean }): Milestone[] {
  const done = ["completed", "invoiced", "paid"].includes(f.status);
  const working = done || f.status === "in_progress";

  return [
    { label: "Called",
      earned: true,
      what: "They got in touch." },
    { label: "Quoted",
      // A free visit offered is the quote, for a trade whose quote is one.
      earned: f.hasPricedLine || Boolean(f.visitQuote),
      what: "A figure they were actually shown." },
    { label: "Approved",
      earned: f.hasAuthorization,
      what: "Signed or spoken — the yes is on file." },
    { label: "Done",
      earned: working && done,
      what: "The work is finished." },
    { label: "Paid",
      earned: f.invoicePaid || f.status === "paid",
      what: "The money is in." },
  ];
}

/** How many are lit, for the one-line version. */
export const starsEarned = (m: Milestone[]): number => m.filter((x) => x.earned).length;
